import { useEffect, useState, useRef } from "react";
import { posChannel } from "./posChannel";
import { Cart, ScreenDetailed } from "../types";
import { getDecryptedToken } from "../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../utils/currency/currencyUtils";
import Promotions from "../../customerview/Promotions";
import { getMediaUrl } from "../../../utils/mediaUrl";

export default function CustomerView() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [now, setNow] = useState<Date>(new Date());
  const [isAnimating, setIsAnimating] = useState(false);
  const previousCartRef = useRef<Cart | null>(null);
  const itemCountRef = useRef(0);

  // Get shop details from token
  const shopName =
    getDecryptedToken("martName") || getDecryptedToken("name") || "Shop";
  const address =
    getDecryptedToken("address") || getDecryptedToken("martAddress") || "";
  const phoneNumber =
    getDecryptedToken("phoneNumber") ||
    getDecryptedToken("martPhone") ||
    getDecryptedToken("phone") ||
    "";
  const ownerName = getDecryptedToken("name") || "";
  const email = getDecryptedToken("email") || "";
  const currency = getDecryptedToken("currency") || "";
  const currencySymbol = getCurrencySymbol();

  // Calculate totals from cart items
  const calculateTotals = () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { total: 0, discount: 0, tax: 0, netTotal: 0, itemCount: 0 };
    }

    const total = cart.items.reduce((sum, item) => sum + item.total, 0);
    const netTotal = cart.grandTotal;
    const discount = Math.max(0, total - netTotal);
    const tax = 0;
    const itemCount = cart.items.reduce((sum, item) => sum + item.qty, 0);

    return { total, discount, tax, netTotal, itemCount };
  };

  const totals = calculateTotals();

  // Animate on cart update
  useEffect(() => {
    if (cart && previousCartRef.current) {
      const prevCount = previousCartRef.current.items?.length || 0;
      const newCount = cart.items?.length || 0;

      if (newCount !== prevCount) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 500);
      }
    }
    previousCartRef.current = cart;
    itemCountRef.current = totals.itemCount;
  }, [cart, totals.itemCount]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Request fullscreen function
  const requestFullscreen = async () => {
    const isFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    if (isFullscreen) return;

    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        await (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).mozRequestFullScreen) {
        await (document.documentElement as any).mozRequestFullScreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        await (document.documentElement as any).msRequestFullscreen();
      }
    } catch (error) {
      console.log("Fullscreen request failed:", error);
    }
  };

  useEffect(() => {
    const handleMessage = (e: MessageEvent<Cart>) => {
      setCart(e.data);
    };

    posChannel.onmessage = handleMessage;

    const handleWindowMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === "REQUEST_FULLSCREEN") {
        requestFullscreen();
      }
    };

    window.addEventListener("message", handleWindowMessage);

    return () => {
      posChannel.onmessage = null;
      window.removeEventListener("message", handleWindowMessage);
    };
  }, []);

  useEffect(() => {
    const setupAutoFullscreen = () => {
      const handleAnyInteraction = () => requestFullscreen();

      document.addEventListener("click", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("touchstart", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("mousedown", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("keydown", handleAnyInteraction, {
        once: true,
        capture: true,
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => requestFullscreen());
      });

      setTimeout(() => requestFullscreen(), 0);
      setTimeout(() => requestFullscreen(), 100);
      setTimeout(() => requestFullscreen(), 300);
      setTimeout(() => requestFullscreen(), 500);
    };

    const setupFullScreenStyles = () => {
      document.documentElement.style.width = "100%";
      document.documentElement.style.height = "100%";
      document.documentElement.style.margin = "0";
      document.documentElement.style.padding = "0";
      document.documentElement.style.overflow = "hidden";

      if (document.body) {
        document.body.style.width = "100%";
        document.body.style.height = "100%";
        document.body.style.margin = "0";
        document.body.style.padding = "0";
        document.body.style.overflow = "hidden";
      }
    };

    setupFullScreenStyles();
    setupAutoFullscreen();

    if (document.readyState === "complete") {
      setupAutoFullscreen();
    } else {
      window.addEventListener("load", setupAutoFullscreen);
      document.addEventListener("DOMContentLoaded", setupAutoFullscreen);
    }

    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      if (!isFullscreen) {
        setTimeout(() => requestFullscreen(), 100);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
      document.removeEventListener("load", setupAutoFullscreen);
      document.removeEventListener("DOMContentLoaded", setupAutoFullscreen);
    };
  }, []);

  // Empty state
  if (!cart || cart.items.length === 0) {
    return (
      <div
        className="customer-screen"
        onClick={() => requestFullscreen()}
        onTouchStart={() => requestFullscreen()}
        style={{
          cursor: "pointer",
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "#f8f9fa",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          padding: "40px",
          gap: "40px",
        }}
      >
        {/* Welcome Message */}
        <div
          style={{
            textAlign: "center",
            maxWidth: "800px",
            animation: "fadeIn 0.8s ease-out",
          }}
        >
          <h1
            style={{
              fontSize: "3.5rem",
              fontWeight: 700,
              color: "#0a2398",
              margin: "0 0 20px 0",
              letterSpacing: "-1px",
            }}
          >
            Welcome!
          </h1>
          <p
            style={{
              fontSize: "1.5rem",
              color: "#666",
              margin: "0 0 10px 0",
              fontWeight: 400,
              lineHeight: "1.6",
            }}
          >
            Thank you for visiting {shopName}
          </p>
          <p
            style={{
              fontSize: "1.2rem",
              color: "#999",
              margin: "0",
              fontWeight: 300,
              fontStyle: "italic",
            }}
          >
            Your order will appear here once items are added
          </p>
        </div>

        {/* Promotions */}
        <div
          style={{
            width: "100vw",
            height: "100vh",
            position: "absolute",
            top: 0,
            left: 0,
            animation: "fadeIn 1s ease-out",
          }}
        >
          <Promotions />
        </div>
      </div>
    );
  }

  return (
    <div
      className="customer-screen customer-view-container"
      onClick={() => requestFullscreen()}
      onTouchStart={() => requestFullscreen()}
      style={{
        cursor: "pointer",
        width: "100%",
        height: "100vh",
        background: "#f8f9fa",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        overflow: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .cart-item {
          animation: slideIn 0.3s ease-out;
          transition: all 0.2s ease;
        }
        
        .cart-item:hover {
          background-color: #f8f9fa;
          transform: translateX(4px);
        }
        
        .summary-card {
          animation: fadeIn 0.5s ease-out;
        }
        
        .item-badge {
          animation: pulse 0.6s ease-in-out;
        }
        
        .table-wrapper {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f1f1f1;
        }
        
        .table-wrapper::-webkit-scrollbar {
          width: 8px;
        }
        
        .table-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .table-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        
        .table-wrapper::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>

      {/* Item count badge */}
      {totals.itemCount > 0 && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 100,
            background: "#fff",
            color: "#333",
            padding: "10px 18px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            fontSize: "14px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: "1px solid #e0e0e0",
            animation: isAnimating ? "pulse 0.6s ease-in-out" : "none",
            transition: "all 0.3s ease",
          }}
          className="item-badge"
        >
          <span
            style={{
              background: "#0a2398",
              color: "#fff",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 700,
            }}
          >
            {cart.items.length}
          </span>
          <span>Items</span>
        </div>
      )}

      <div
        className="split-grid"
        style={{
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          padding: "32px",
          gap: "32px",
          boxSizing: "border-box",
          maxWidth: "1920px",
          margin: "0 auto",
        }}
      >
        {/* Left: Order table and summary */}
        <div
          className="left-column left-scroll"
          style={{
            width: "55%",
            maxWidth: "800px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
            justifyContent: "flex-start",
            boxSizing: "border-box",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Title with icon */}
          <div
            style={{
              width: "100%",
              textAlign: "center",
              marginBottom: "8px",
            }}
          >
            <h2
              style={{
                fontSize: "2.5rem",
                fontWeight: 700,
                margin: 0,
                color: "#0a2398",
                letterSpacing: "-0.5px",
              }}
            >
              Your Order
            </h2>
            {totals.itemCount > 0 && (
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: "#666",
                  fontSize: "1rem",
                  fontWeight: 400,
                }}
              >
                {totals.itemCount} {totals.itemCount === 1 ? "item" : "items"}{" "}
                in your cart
              </p>
            )}
          </div>

          {/* Table wrapper with smooth scroll */}
          <div
            className="table-wrapper customer-table-wrapper"
            style={{
              width: "100%",
              maxWidth: "800px",
              overflowX: "auto",
              overflowY: "auto",
              background: "#fff",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              margin: "0",
              maxHeight: "calc(100vh - 400px)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            <table
              className="customer-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                margin: 0,
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "left",
                      background: "#0a2398",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Item
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "center",
                      background: "#0a2398",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Qty
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "right",
                      background: "#0a2398",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Price
                  </th>
                  <th
                    style={{
                      padding: "18px 16px",
                      textAlign: "right",
                      background: "#0a2398",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {cart.items.map((item, index) => (
                  <tr
                    key={item.id}
                    className="cart-item"
                    style={{
                      animationDelay: `${index * 0.05}s`,
                    }}
                  >
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {item.image && (
                          <img
                            src={getMediaUrl(item.image)}
                            alt={item.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              flexShrink: 0,
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                              border: "2px solid #f0f0f0",
                            }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            color: "#2d3748",
                          }}
                        >
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: "1.15rem",
                        fontWeight: 700,
                        color: "#0a2398",
                      }}
                    >
                      <span
                        style={{
                          background: "#0a2398",
                          color: "#fff",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          display: "inline-block",
                          minWidth: "50px",
                          fontSize: "1.1rem",
                        }}
                      >
                        {item.qty}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: "1.15rem",
                        fontWeight: 600,
                        color: "#2d3748",
                      }}
                    >
                      <span style={{ color: "#0a2398", fontWeight: 700 }}>
                        {currency} {(item.total / item.qty).toFixed(2)}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: "1.2rem",
                        fontWeight: 700,
                        color: "#0a2398",
                      }}
                    >
                      {currency} {item.total.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary card */}
          <div
            className="summary-card customer-summary"
            style={{
              width: "100%",
              maxWidth: "800px",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              padding: "24px 32px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              fontSize: "1.1rem",
              boxSizing: "border-box",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              className="summary-row"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                padding: "8px 0",
                boxSizing: "border-box",
                fontSize: "1rem",
                color: "#666",
              }}
            >
              <span style={{ fontWeight: 500 }}>Subtotal:</span>
              <span style={{ fontWeight: 600, color: "#333" }}>
                {currency} {totals.total.toFixed(2)}
              </span>
            </div>
            {totals.discount > 0 && (
              <div
                className="summary-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "8px 0",
                  boxSizing: "border-box",
                  fontSize: "1rem",
                  color: "#10b981",
                }}
              >
                <span style={{ fontWeight: 500 }}>Discount:</span>
                <span style={{ fontWeight: 600 }}>
                  - {currency} {totals.discount.toFixed(2)}
                </span>
              </div>
            )}
            {totals.tax > 0 && (
              <div
                className="summary-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "8px 0",
                  boxSizing: "border-box",
                  fontSize: "1rem",
                  color: "#666",
                }}
              >
                <span style={{ fontWeight: 500 }}>Tax:</span>
                <span style={{ fontWeight: 600, color: "#333" }}>
                  {currency} {totals.tax.toFixed(2)}
                </span>
              </div>
            )}
            <div
              style={{
                height: "2px",
                background:
                  "linear-gradient(90deg, transparent, #e0e0e0, transparent)",
                margin: "8px 0",
              }}
            />
            <div
              className="summary-row"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                padding: "12px 0",
                boxSizing: "border-box",
                fontWeight: 700,
                fontSize: "1.5rem",
                color: "#0a2398",
                borderTop: "2px solid #e0e0e0",
                marginTop: "4px",
                paddingTop: "16px",
              }}
            >
              <span>Total Amount:</span>
              <span>
                {currency} {totals.netTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Shop details and promotions */}
        <aside
          className="right-panel"
          style={{
            width: "45%",
            height: "100%",
            boxSizing: "border-box",
            borderRadius: "12px",
            background: "#fff",
            padding: "32px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          {/* Shop header */}
          <div
            className="shop-header"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              marginBottom: "24px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              className="shop-logo"
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "12px",
                background: "#0a2398",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.75rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {shopName?.slice(0, 2).toUpperCase()}
            </div>
            <div
              className="shop-info"
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <div
                className="shop-name"
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#2d3748",
                  marginBottom: "4px",
                }}
              >
                {shopName}
              </div>
              {ownerName && (
                <div
                  className="shop-owner"
                  style={{
                    fontSize: "0.9rem",
                    color: "#718096",
                    fontWeight: 500,
                  }}
                >
                  Cashier: {ownerName}
                </div>
              )}
            </div>
          </div>

          {/* Shop details */}
          <div
            className="shop-details"
            style={{
              width: "100%",
              fontSize: "0.95rem",
              color: "#4a5568",
              boxSizing: "border-box",
              marginBottom: "24px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {address && (
              <div
                className="shop-address"
                style={{
                  whiteSpace: "pre-line",
                  marginBottom: "12px",
                  lineHeight: "1.6",
                  padding: "12px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                {address}
              </div>
            )}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {phoneNumber && (
                <div
                  className="shop-phone"
                  style={{
                    padding: "8px 12px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {phoneNumber}
                </div>
              )}
              {email && (
                <div
                  className="shop-email"
                  style={{
                    padding: "8px 12px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {email}
                </div>
              )}
              <div
                className="shop-date"
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  padding: "8px 12px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  fontWeight: 500,
                  border: "1px solid #e0e0e0",
                }}
              >
                {now.toLocaleString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </div>
            </div>
          </div>

          {/* Promotions */}
          <div
            className="promo"
            style={{
              marginTop: "auto",
              width: "100%",
              borderRadius: "12px",
              overflow: "hidden",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f8f9fa",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e0e0e0",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Promotions isInline={true} />
          </div>

          {/* Footer */}
          <div
            className="currency-footer"
            style={{
              marginTop: "20px",
              fontSize: "0.85rem",
              color: "#666",
              borderTop: "1px dashed #e0e0e0",
              paddingTop: "16px",
              width: "100%",
              textAlign: "center",
              boxSizing: "border-box",
              fontWeight: 500,
              position: "relative",
              zIndex: 1,
            }}
          >
            <a
              href="https://visolabs.io/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0a2398",
                textDecoration: "none",
                transition: "color 0.2s ease",
              }}
            >
              visolabs.io
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
